import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CustomLoading } from "components/CustomLoading";
import { FileText, HelpCircle, Link2, FileType2, FolderClosed } from "lucide-react";
import { formatRelative } from "./category-rows";

interface Category {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
}

interface SearchResult {
    resultType: "category" | "page" | "question" | "link" | "document";
    id: string;
    title: string | null;
    snippet: string | null;
    categoryId: string | null;
    createdAt: string;
}

interface Facets {
    total: number;
    byType: { category: number; page: number; question: number; link: number; document: number };
    byCategory: { id: string; count: number }[];
}

interface Props {
    query: string;
    type?: string;
    cats?: string[];
    categories: Category[];
    onSelectPage: (pageId: string, categoryId: string) => void;
    onSelectQuestion: (questionId: string) => void;
    onSelectCategory: (categoryId: string) => void;
    onChangeFilters: (type: string | undefined, cats: string[] | undefined) => void;
}

const CATEGORY_COLLAPSE_THRESHOLD = 10;

export default function SearchView({
    query, type, cats, categories, onSelectPage, onSelectQuestion, onSelectCategory, onChangeFilters,
}: Props) {
    const [loadingResults, setLoadingResults] = useState(true);
    const [results, setResults] = useState<SearchResult[]>([]);
    const [facets, setFacets] = useState<Facets | null>(null);
    const [catSearch, setCatSearch] = useState("");
    const [showAllCats, setShowAllCats] = useState(false);

    const categoriesById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);
    const activeCats = cats || [];

    useEffect(() => {
        if (!query) return;
        setLoadingResults(true);
        const typeParam = type && type !== "all" ? `&type=${type}` : "";
        const catParam = activeCats.length ? activeCats.map(id => `&categoryIds=${id}`).join("") : "";
        axios.get(`/api/manuals/search?q=${encodeURIComponent(query)}${typeParam}${catParam}&take=100`)
            .then(res => {
                if (res.data.success) setResults(res.data.items);
                else toast.error(res.data.message || "Search failed");
            })
            .catch(() => toast.error("Search failed"))
            .finally(() => setLoadingResults(false));
    }, [query, type, activeCats.join(",")]);

    useEffect(() => {
        // facets describe "what's available for this query" — intentionally NOT filtered by
        // the current type/cats selections, so counts stay visible next to filters the user
        // hasn't applied yet. do not add type/activeCats to deps.
        if (!query) return;
        axios.get(`/api/manuals/search/facets?q=${encodeURIComponent(query)}`)
            .then(res => {
                if (res.data.success) setFacets(res.data as Facets);
            })
            .catch(() => { /* facets are best-effort */ });
    }, [query]);

    const contentTypeOptions = [
        { key: "all", label: "Everything", count: facets?.total ?? 0 },
        { key: "category", label: "Categories", count: facets?.byType?.category ?? 0 },
        { key: "page", label: "Pages", count: facets?.byType?.page ?? 0 },
        { key: "question", label: "Questions", count: facets?.byType?.question ?? 0 },
        { key: "link", label: "Links", count: facets?.byType?.link ?? 0 },
        { key: "document", label: "Documents", count: facets?.byType?.document ?? 0 },
    ];

    const catCounts = useMemo(() => {
        const m = new Map<string, number>();
        (facets?.byCategory || []).forEach(f => m.set(f.id, f.count));
        return m;
    }, [facets]);

    const visibleCats = useMemo(() => {
        const q = catSearch.trim().toLowerCase();
        const withCounts = categories
            .map(c => ({ cat: c, count: catCounts.get(c.id) ?? 0 }))
            .filter(x => x.count > 0 || activeCats.includes(x.cat.id))
            .filter(x => q === "" || x.cat.name.toLowerCase().includes(q));
        withCounts.sort((a, b) => b.count - a.count);
        return withCounts;
    }, [categories, catCounts, catSearch, activeCats.join(",")]);

    const catsToRender = showAllCats || visibleCats.length <= CATEGORY_COLLAPSE_THRESHOLD
        ? visibleCats
        : visibleCats.slice(0, CATEGORY_COLLAPSE_THRESHOLD);

    function toggleCategory(id: string) {
        const next = activeCats.includes(id)
            ? activeCats.filter(x => x !== id)
            : [...activeCats, id];
        onChangeFilters(type, next.length ? next : undefined);
    }

    function setType(next: string) {
        onChangeFilters(next === "all" ? undefined : next, activeCats.length ? activeCats : undefined);
    }

    const shownResults = results;
    const total = facets?.total ?? results.length;

    return (
        <div className="m-cat-page m-search-page">
            <div className="m-cat-header">
                <div><h1 className="m-cat-title">Search</h1></div>
            </div>

            <div className="m-search-layout">
                <aside className="m-search-filters">
                    <h3 className="m-search-filters-head">Filter by</h3>

                    <div className="m-search-filter-group">
                        <label className="m-search-filter-label">Content type</label>
                        <select
                            className="m-input"
                            value={type || "all"}
                            onChange={e => setType(e.target.value)}
                        >
                            {contentTypeOptions.map(o => (
                                <option key={o.key} value={o.key}>{o.label} ({o.count})</option>
                            ))}
                        </select>
                    </div>

                    <div className="m-search-filter-group">
                        <label className="m-search-filter-label">Categories</label>
                        <input
                            className="m-input m-search-cat-search"
                            placeholder="Search categories"
                            value={catSearch}
                            onChange={e => setCatSearch(e.target.value)}
                        />
                        {catsToRender.length === 0 ? (
                            <div className="m-search-cat-empty">No categories with matches.</div>
                        ) : (
                            <div className="m-search-cat-list">
                                {catsToRender.map(({ cat, count }) => (
                                    <label key={cat.id} className="m-search-cat-row">
                                        <input
                                            type="checkbox"
                                            checked={activeCats.includes(cat.id)}
                                            onChange={() => toggleCategory(cat.id)}
                                        />
                                        <span className="m-search-cat-name" style={{ color: cat.color || undefined }}>{cat.name}</span>
                                        <span className="m-search-cat-count">{count}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                        {visibleCats.length > CATEGORY_COLLAPSE_THRESHOLD && (
                            <button
                                type="button"
                                className="m-search-cat-more"
                                onClick={() => setShowAllCats(v => !v)}
                            >
                                {showAllCats ? "View less" : `View more (${visibleCats.length - CATEGORY_COLLAPSE_THRESHOLD})`}
                            </button>
                        )}
                    </div>
                </aside>

                <section className="m-search-results">
                    <div className="m-search-results-head">
                        {loadingResults ? "Searching…" : (
                            <>{total.toLocaleString()} {total === 1 ? "result" : "results"} for "{query}"</>
                        )}
                    </div>
                    {loadingResults ? (
                        <div className="m-loading"><CustomLoading /></div>
                    ) : shownResults.length === 0 ? (
                        <div className="m-profile-empty">No matches. Try fewer filters or different keywords.</div>
                    ) : (
                        <div className="m-search-result-list">
                            {shownResults.map(r => {
                                const isCategory = r.resultType === "category";
                                const cat = !isCategory && r.categoryId ? categoriesById.get(r.categoryId) : null;
                                return (
                                    <button
                                        key={`${r.resultType}-${r.id}`}
                                        className="m-search-result-row"
                                        onClick={() => {
                                            if (r.resultType === "page") onSelectPage(r.id, r.categoryId || "");
                                            else if (r.resultType === "question") onSelectQuestion(r.id);
                                            else if (r.resultType === "category") onSelectCategory(r.id);
                                        }}
                                    >
                                        <span className="m-search-result-icon">{iconFor(r.resultType)}</span>
                                        <div className="m-search-result-main">
                                            <div className="m-search-result-title">{r.title || "(untitled)"}</div>
                                            <div className="m-search-result-meta">
                                                {typeLabel(r.resultType)}
                                                {cat && <> in <span style={{ color: cat.color || undefined }}>{cat.name}</span></>}
                                                {" · last updated "}{formatRelative(r.createdAt)}
                                            </div>
                                            {r.snippet && (
                                                <div className="m-search-result-snippet">{truncate(stripHtml(r.snippet), 200)}</div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

function iconFor(t: string) {
    if (t === "category") return <FolderClosed size={18} color="#3B82F6" />;
    if (t === "page") return <FileText size={18} color="#1e85e5" />;
    if (t === "question") return <HelpCircle size={18} color="#dc2626" />;
    if (t === "link") return <Link2 size={18} color="#64748b" />;
    if (t === "document") return <FileType2 size={18} color="#64748b" />;
    return <FileText size={18} />;
}

function typeLabel(t: string): string {
    if (t === "category") return "Category";
    if (t === "page") return "Page";
    if (t === "question") return "Question";
    if (t === "link") return "Link";
    if (t === "document") return "Document";
    return t;
}

function stripHtml(s: string): string {
    if (!s) return "";
    return s.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function truncate(s: string, max: number): string {
    if (!s) return "";
    if (s.length <= max) return s;
    return s.slice(0, max).trimEnd() + "…";
}
